/**
 * The Privacy tab of a proposal's or publication's settings: how its text is kept, and every time it left the
 * team (sent to the AI provider, viewed by Otable staff). Members only (the endpoint answers 404 to anyone else).
 * The wording states only what is true today: encrypted at rest, not end-to-end.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import {
  AutoAwesome as AiIcon, AdminPanelSettings as StaffIcon, Settings as SystemIcon, Lock as LockIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

const KIND_ICON = { ai: AiIcon, staff: StaffIcon, system: SystemIcon };

export default function DocumentPrivacyPanel({ proposalId, docLabel = 'Proposal' }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!proposalId) return undefined;
    let live = true;
    axiosService.get(`/proposal/proposals/${proposalId}/access-log/`)
      .then((res) => { if (live) setRows(res.data || []); })
      .catch(() => { if (live) setError('The access log could not be loaded.'); });
    return () => { live = false; };
  }, [proposalId]);

  const doc = docLabel.toLowerCase();
  return (
    <Stack spacing={2}>
      <Alert icon={<LockIcon />} severity="info" variant="outlined">
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          This {doc} is private to your team; Otable staff cannot open it.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Only the members can read it. Its text (sections, abstract, comments, messages and saved versions) is
          stored encrypted, so a copy of the database or of a backup cannot be read. The text leaves the team only
          when a member uses an AI feature; each time it is listed below.
        </Typography>
      </Alert>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>When the text left the team</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && rows === null && <CircularProgress size={20} />}
        {rows && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Never: no AI feature has been used on this {doc}.
          </Typography>
        )}
        {rows && rows.length > 0 && (
          <List dense disablePadding>
            {rows.map((r) => {
              const Icon = KIND_ICON[r.kind] || SystemIcon;
              return (
                <ListItem key={r.id} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}><Icon fontSize="small" color="action" /></ListItemIcon>
                  <ListItemText
                    primary={r.detail || r.kind_label}
                    secondary={`${r.user?.name || 'Someone'} · ${new Date(r.created).toLocaleString()}`} />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Stack>
  );
}
